import { useState, useEffect, useMemo } from 'react'
import './SolutionMatrix.css'
import { API_BASE as API_BASE_CONFIG } from '../../config'
import { useAuth } from '../../AuthProvider'
import { providedThroughText } from '../../utils/providedThrough'

const API_BASE = API_BASE_CONFIG

const CAT_META = {
  'Payroll':                 { icon: '💰', cls: 'cat-payroll' },
  'Benefits':                { icon: '🛡', cls: 'cat-benefits' },
  'Time & Attendance':       { icon: '⏱', cls: 'cat-time' },
  'Talent Management':       { icon: '⭐', cls: 'cat-talent' },
  'Recruiting & Onboarding': { icon: '🤝', cls: 'cat-recruiting' },
  'AI & Analytics':          { icon: '🧠', cls: 'cat-ai' },
  'Employee Experience':     { icon: '😊', cls: 'cat-experience' },
  'System & Admin':          { icon: '⚙', cls: 'cat-system' },
  'Workforce Management':    { icon: '📊', cls: 'cat-wfm' },
  'ACA Compliance':          { icon: '📋', cls: 'cat-aca' },
  'Managed Services':        { icon: '🏢', cls: 'cat-managed' },
  'Canada & Global':         { icon: '🌎', cls: 'cat-canada' },
  'Specialty':               { icon: '🏟', cls: 'cat-specialty' },
  'Other':                   { icon: '📦', cls: 'cat-other' },
  'Unknown':                 { icon: '❓', cls: 'cat-unknown' },
}

// `portal`: rendered in the customer portal's System tab. A non-admin there (the customer,
// or a consultant previewing) gets the customer view (Scott, 2026-10-06): every catalog
// item, each labelled "Contracted" (a registered key) or "Not contracted", with the
// per-category counts, the summary and coverage figures, and the All / Contracted /
// Not contracted / Products / Services filter. It does not get the unknown-keys catalog
// warning or the upload control, and unknown registered keys keep their plain wording.
// Admins, and the internal customer page, keep the matrix in its Registered / Available
// wording.
export default function SolutionMatrix({ customerId, vendorLabel = '', portal = false }) {
  const { isAdmin } = useAuth()
  const customerView = portal && !isAdmin
  const yes = customerView ? 'Contracted' : 'Registered'
  const no  = customerView ? 'Not contracted' : 'Available'
  const [reference, setReference]       = useState(null)
  const [matrix, setMatrix]             = useState(null)
  const [loading, setLoading]           = useState(true)
  const [error, setError]               = useState(null)
  const [filter, setFilter]             = useState('all')
  const [search, setSearch]             = useState('')
  const [openCats, setOpenCats]         = useState(new Set())
  const [uploading, setUploading]       = useState(false)
  const [uploadResult, setUploadResult] = useState(null)
  const [reloadKey, setReloadKey]       = useState(0)
  // The customer's products that come through this catalog's vendor (resold or
  // embedded): the vendor's product keys do not name them, so they are listed beside.
  const [provided, setProvided]         = useState([])


  const token = localStorage.getItem('xlr8_token') || ''
  const headers = { 'Authorization': `Bearer ${token}` }

  useEffect(() => {
    async function load() {
      setLoading(true)
      setError(null)
      try {
        const [refRes, matRes] = await Promise.all([
          fetch(`${API_BASE}/api/products/reference/categories`, { headers }),
          fetch(`${API_BASE}/api/customers/${customerId}/solution-matrix`, { headers }),
        ])
        if (refRes.ok) setReference(await refRes.json())
        // A failed read of the customer's keys is an error, never "no product keys".
        if (!matRes.ok) {
          const body = await matRes.text()
          throw new Error(`API ${matRes.status}: ${body}`)
        }
        const data = await matRes.json()
        if (data.has_customer_data) setMatrix(data)
        setProvided(Array.isArray(data.provided_through) ? data.provided_through : [])
      } catch (e) {
        setError(e.message)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [customerId, reloadKey])

  async function handleUpload(e) {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true); setUploadResult(null)
    try {
      const form = new FormData()
      form.append('file', file)
      const res = await fetch(`${API_BASE}/api/customers/${customerId}/solution-matrix/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: form,
      })
      const data = await res.json()
      setUploadResult(data)
      if (data.codes_saved > 0) setReloadKey(k => k + 1)
    } catch (err) {
      setUploadResult({ message: 'Upload failed: ' + err.message })
    } finally {
      setUploading(false)
      e.target.value = ''
    }
  }

  // ── Build categories ────────────────────────────────────────────────────────
  const categories = useMemo(() => {
    if (!reference?.categories) return []

    if (matrix?.matrix) {
      // The customer view files unknown registered keys under Other, beside the catalog's
      // own uncategorised keys: one Other section, not two.
      const byName = {}
      Object.entries(matrix.matrix).forEach(([catName, catData]) => {
        const name = customerView && catName === 'Unknown' ? 'Other' : catName
        const into = byName[name] || (byName[name] = { registered: [], available: [] })
        into.registered.push(...(catData.registered || []))
        into.available.push(...(catData.available || []))
      })
      return Object.entries(byName).map(([catName, catData]) => {
        const meta = CAT_META[catName] || CAT_META['Other']
        const registered = catData.registered
        const available = catData.available
        const all = [...registered, ...available]
        return {
          name: catName, ...meta,
          items: all, registered, available,
          registeredCount: registered.length,
          availableCount: available.length,
          registeredProducts: registered.filter(i => i.product_type === 'product').length,
          registeredServices: registered.filter(i => i.product_type === 'service').length,
          totalCount: all.length,
          coveragePct: all.length > 0 ? Math.round(registered.length / all.length * 100) : 0,
        }
      }).sort((a, b) => b.registeredCount - a.registeredCount || a.name.localeCompare(b.name))
    }

    return Object.entries(reference.categories).map(([catName, catData]) => {
      const meta = CAT_META[catName] || CAT_META['Other']
      const items = catData.items || catData
      const itemList = Array.isArray(items) ? items : []
      return {
        name: catName, ...meta,
        items: itemList.map(i => ({ ...i, registered: false })),
        registered: [], available: itemList,
        registeredCount: 0, availableCount: itemList.length,
        registeredProducts: 0, registeredServices: 0,
        totalCount: itemList.length, coveragePct: 0,
      }
    }).sort((a, b) => a.name.localeCompare(b.name))
  }, [reference, matrix, customerView])

  const summary = useMemo(() => {
    // The category figures count the sections shown (Unknown folded into Other).
    if (matrix?.summary && customerView) return {
      ...matrix.summary,
      categories_with_products: categories.filter(c => c.registeredCount > 0).length,
      total_categories: categories.length,
    }
    if (matrix?.summary) return matrix.summary
    const total = categories.reduce((a, c) => a + c.totalCount, 0)
    return {
      registered_count: 0, registered_products: 0, registered_services: 0,
      available_count: total, coverage_pct: 0,
      categories_with_products: 0, total_categories: categories.length,
    }
  }, [matrix, categories, customerView])

  const filteredCategories = useMemo(() => {
    const q = search.toLowerCase()
    return categories.map(cat => {
      let items = cat.items
      if (filter === 'registered') items = items.filter(i => i.registered)
      else if (filter === 'available') items = items.filter(i => !i.registered)
      else if (filter === 'products') items = items.filter(i => i.product_type === 'product')
      else if (filter === 'services') items = items.filter(i => i.product_type === 'service')
      if (q) items = items.filter(i => i.code.toLowerCase().includes(q) || i.name.toLowerCase().includes(q))
      return { ...cat, filteredItems: items }
    }).filter(cat => cat.filteredItems.length > 0)
  }, [categories, filter, search])

  const toggleCat = (name) => {
    setOpenCats(prev => {
      const next = new Set(prev)
      next.has(name) ? next.delete(name) : next.add(name)
      return next
    })
  }

  if (loading) return <div className="sm-loading">Loading solution matrix…</div>
  if (error)   return <div className="sm-error">Failed to load: {error}</div>

  const hasCustomerData = matrix?.has_customer_data

  const providedStrip = provided.length > 0 && (
    <div className="sm-provided">
      <div className="sm-provided-title">{customerView ? 'Also provided' : 'Resold or embedded by this vendor'}</div>
      {provided.map((p, i) => (
        <div key={i} className="sm-provided-row">
          <span className="sm-provided-name">{p.product_name}</span>
          {p.functions?.length > 0 && <span className="sm-provided-fns">{p.functions.map(f => f.replace(/_/g, ' ')).join(', ')}</span>}
          <span className="sm-status-badge provided">{providedThroughText(p, [], { customer: customerView })}</span>
        </div>
      ))}
    </div>
  )

  if (customerView && !hasCustomerData) {
    return <div className="sm-root"><div className="sm-no-data"><div style={{ flex: 1 }}><strong>No product keys loaded yet.</strong></div></div>{providedStrip}</div>
  }

  return (
    <div className="sm-root">
      {providedStrip}

      {/* Summary Strip */}
      <div className="sm-summary-strip">
        <div className="sm-summary-card">
          <div className="sm-summary-label">{yes}</div>
          <div className="sm-summary-value green">{summary.registered_count}</div>
          <div className="sm-summary-detail">{summary.registered_products} products · {summary.registered_services} services</div>
        </div>
        <div className="sm-summary-card">
          <div className="sm-summary-label">{no}</div>
          <div className="sm-summary-value blue">{summary.available_count}</div>
          <div className="sm-summary-detail">{customerView ? 'In the catalog, not on your contract' : 'Not yet subscribed'}</div>
        </div>
        <div className="sm-summary-card">
          <div className="sm-summary-label">Categories</div>
          <div className="sm-summary-value amber">{summary.categories_with_products} / {summary.total_categories}</div>
          <div className="sm-summary-detail">With {yes.toLowerCase()} items</div>
        </div>
        <div className="sm-summary-card">
          <div className="sm-summary-label">Coverage</div>
          <div className="sm-summary-value green">{summary.coverage_pct}%</div>
          <div className="sm-summary-detail">{customerView ? 'Of the' : 'Of available'} {vendorLabel || 'product'} suite</div>
        </div>
      </div>

      {/* Coverage Bar */}
      <div className="sm-coverage-bar">
        <div className="sm-coverage-header">
          <span className="sm-coverage-title">Suite Coverage</span>
          <span className="sm-coverage-pct">{summary.registered_count} of {summary.registered_count + summary.available_count}</span>
        </div>
        <div className="sm-coverage-track">
          <div className="sm-coverage-fill" style={{ width: `${summary.coverage_pct}%` }} />
        </div>
        <div className="sm-coverage-legend">
          <span><span className="sm-legend-dot registered" /> {yes} ({summary.registered_count})</span>
          <span><span className="sm-legend-dot available" /> {no} ({summary.available_count})</span>
        </div>
      </div>

      {/* No Data Banner */}
      {!hasCustomerData && (
        <div className="sm-no-data">
          <span className="sm-no-data-icon">📡</span>
          <div style={{ flex: 1 }}>
            <strong>No product keys loaded.</strong> Sync via RaaS or upload a file containing product key codes.
          </div>
          <label className="sm-upload-btn">
            {uploading ? 'Uploading…' : '⬆ Upload File'}
            <input type="file" accept=".xlsx,.xls,.csv" onChange={handleUpload} hidden disabled={uploading} />
          </label>
        </div>
      )}
      {!customerView && matrix?.unrecognized?.length > 0 && (
        <div className="sm-upload-result info">
          ⚠ Unknown key{matrix.unrecognized.length === 1 ? '' : 's'}: {matrix.unrecognized.length} registered
          product key{matrix.unrecognized.length === 1 ? ' is' : 's are'} not in the {vendorLabel || 'vendor'} product
          catalog{matrix.catalog_generated ? ` (as of ${matrix.catalog_generated})` : ''}. Listed under Unknown below.
          <div className="sm-upload-codes">{matrix.unrecognized.join(', ')}</div>
        </div>
      )}
      {uploadResult && (
        <div className={`sm-upload-result ${uploadResult.codes_saved > 0 ? 'success' : 'info'}`}>
          {uploadResult.codes_saved > 0 ? '✓ ' : 'ℹ '}{uploadResult.message}
          {uploadResult.codes_saved > 0 && uploadResult.codes && (
            <div className="sm-upload-codes">{uploadResult.codes.join(', ')}</div>
          )}
          {uploadResult.hint && <div className="sm-upload-hint">{uploadResult.hint}</div>}
          <button className="sm-upload-dismiss" onClick={() => setUploadResult(null)}>✕</button>
        </div>
      )}

      {/* Filter Bar */}
      <div className="sm-filter-bar">
        <div className="sm-filter-pills">
          {[
            { key: 'all',        label: 'All' },
            { key: 'registered', label: `${yes} (${summary.registered_count})` },
            { key: 'available',  label: `${no} (${summary.available_count})` },
            { key: 'products',   label: 'Products' },
            { key: 'services',   label: 'Services' },
          ].map(f => (
            <button key={f.key} className={`sm-filter-pill ${filter === f.key ? 'active' : ''}`} onClick={() => setFilter(f.key)}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="sm-filter-right">
          <input className="sm-filter-search" type="text" placeholder="Search products…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      </div>

      {/* Category Sections */}
      {filteredCategories.map(cat => {
        const isOpen = openCats.has(cat.name) || !!search
        return (
          <div key={cat.name} className={`sm-category ${cat.cls} ${isOpen ? 'open' : ''}`}>
            <div className="sm-cat-header" onClick={() => toggleCat(cat.name)}>
              <div className="sm-cat-left">
                <div className="sm-cat-icon">{cat.icon}</div>
                <div className="sm-cat-name">{cat.name}</div>
              </div>
              <div className="sm-cat-right">
                <div className="sm-cat-counts">
                  <span className="sm-cat-badge registered">{cat.registeredCount} {yes.toLowerCase()}</span>
                  <span className="sm-cat-badge available">{cat.availableCount} {no.toLowerCase()}</span>
                </div>
                <div className="sm-cat-bar-mini">
                  <div className="sm-cat-bar-fill" style={{ width: `${cat.coveragePct}%` }} />
                </div>
                <span className="sm-cat-chevron">▸</span>
              </div>
            </div>

            {isOpen && (
              <div className="sm-product-grid">
                {/* Header row */}
                <div className="sm-grid-header">
                  <span>Code</span>
                  <span>Name</span>
                  <span>Type</span>
                  <span>Status</span>
                </div>

                {cat.filteredItems.map(item => {
                  return (
                    <div key={item.code} className={`sm-product-row ${item.registered ? 'is-registered' : ''}`}>
                      <span className="sm-product-code">{item.code}</span>
                      <span className="sm-product-name">{customerView && item.unknown ? 'Registered product key' : item.name}</span>
                      <span>
                        <span className={`sm-type-badge ${item.product_type}`}>
                          {item.unknown ? (customerView ? '—' : 'Unknown') : item.product_type === 'service' ? 'Service' : 'Product'}
                        </span>
                      </span>
                      <span>
                        {item.unknown && !customerView ? (
                          <span className="sm-status-badge unknown" title="Registered for this customer, but not in the vendor product catalog">
                            <span className="sm-status-dot unknown" />
                            Unknown key
                          </span>
                        ) : (
                          <span className={`sm-status-badge ${item.registered ? 'registered' : 'available'}`}>
                            <span className={`sm-status-dot ${item.registered ? 'registered' : 'available'}`} />
                            {item.registered ? yes : no}
                          </span>
                        )}
                      </span>

                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}

      {filteredCategories.length === 0 && (
        <div className="sm-empty-results">No products match your filter.</div>
      )}
    </div>
  )
}
